import type { Spec } from "@json-render/core";
import type { RenderComponentRegistry } from "@json-render/react-email/render";
import type { RenderComponentRegistry as PdfRenderComponentRegistry } from "@json-render/react-pdf/render";
import { z } from "zod";

export type EmailSpec = Spec;

export const EmailSpecSchema = z
  .object({
    root: z.string(),
    elements: z.record(z.string(), z.unknown()),
  })
  .passthrough() as z.ZodType<EmailSpec>;

export interface EmailRenderOptions {
  state?: Record<string, unknown>;
  registry?: RenderComponentRegistry;
  blockResolver?: TemplateBlockResolver;
  target?: TemplateBlockTarget;
  /**
   * Resolves Image src values that use the `erp-doc://<documentId>` scheme
   * into fetchable URLs (e.g. a SAS URL for previews, a cid: reference at
   * send time). External http(s) URLs are never passed through this hook.
   */
  imageResolver?: ImageSourceResolver;
}

export interface EmailRenderResult {
  html: string;
  plainText: string;
  metadata: EmailRenderMetadata;
}

export interface PdfRenderOptions {
  state?: Record<string, unknown>;
  registry?: PdfRenderComponentRegistry;
  blockResolver?: TemplateBlockResolver;
  /**
   * Resolves Image src values that use the `erp-doc://<documentId>` scheme
   * into fetchable URLs. react-pdf reads the resolved URL directly — unlike
   * email there is no `cid:` send-time path.
   */
  imageResolver?: ImageSourceResolver;
}

export interface PdfRenderResult {
  /** Rendered PDF bytes. Callers serialize (e.g. base64) as needed. */
  pdf: Uint8Array;
  metadata: EmailRenderMetadata;
}

export type TemplateBlockTarget = "email" | "pdf" | "internal_ui" | "brand-kit";

export interface TemplateBlockVersion {
  blockId: string;
  versionId: string;
  version: number;
  jsonSpec: EmailSpec;
}

export interface ResolvedTemplateBlockVersion {
  blockId: string;
  versionId: string;
  version: number;
}

export interface EmailRenderMetadata {
  resolvedBlocks: ResolvedTemplateBlockVersion[];
  /** Document ids referenced via `erp-doc://` Image sources in the rendered spec. */
  referencedDocumentIds: string[];
}

export type ImageSourceResolver = (documentId: string) => Promise<string>;

export type TemplateBlockResolver = (
  blockId: string,
  target: TemplateBlockTarget,
) => Promise<TemplateBlockVersion>;
